import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatNumberToExcelUsage } from '@commons/DisplayNumber';
import utilsXLS from '@commons/utils/makeXLS';

const getSupplierProductsSheetColumns = (currency) => [
  {
    propertyKey: 'storeName',
    name: i18next.t('GENERAL.STORE'),
  },
  {
    propertyKey: 'name',
    name: i18next.t('GENERAL.NAME'),
  },
  {
    propertyKey: 'supplierName',
    name: i18next.t('GENERAL.SUPPLIER'),
  },
  {
    propertyKey: 'selectedPackaging',
    name: i18next.t('GENERAL.PACKAGING'),
  },
  {
    propertyKey: 'quantity',
    name: i18next.t('GENERAL.QUANTITY'),
  },
  {
    propertyKey: 'category',
    name: i18next.t('GENERAL.CATEGORY'),
  },
  {
    name: i18next.t('GENERAL.COST_EX_TAX'),
    propertyKey: 'cost',
    type: 'currency',
  },
  {
    propertyKey: 'total',
    name: i18next.t('GENERAL.TOTAL_EX_TAX'),
    transform: (value) => formatNumberToExcelUsage(value, currency.numberDecimals),
  },
  {
    propertyKey: 'timestamp',
    name: i18next.t('GENERAL.DATE'),
  },
];

export const formattedSupplierProductsSheetData = (supplierProductsLosses, lossCategories) =>
  supplierProductsLosses.map(
    ({
      name,
      supplierName,
      packagings,
      supplierProductPackagingId,
      quantity,
      lossCategoryId,
      cost,
      total,
      timestamp,
      storeName,
    }) => {
      const selectedPackaging = packagings.find(({ id }) => id === supplierProductPackagingId);

      const category = lossCategories.find(({ id }) => id === lossCategoryId);

      return {
        storeName,
        name,
        supplierName,
        selectedPackaging: selectedPackaging.name,
        quantity,
        category: category.name || i18next.t('GENERAL.NONE_VALUE'),
        cost,
        total,
        timestamp: moment(timestamp).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
      };
    },
  );

const getRecipesSheetColumns = (isCentralMode, hasMultipleChannels, currency) => {
  const columns = [
    {
      propertyKey: 'storeName',
      name: i18next.t('GENERAL.STORE'),
    },
    {
      propertyKey: 'name',
      name: i18next.t('GENERAL.NAME'),
    },
    {
      propertyKey: 'quantity',
      name: i18next.t('GENERAL.QUANTITY'),
    },
    {
      propertyKey: 'unit',
      name: i18next.t('GENERAL.UNIT'),
    },
    {
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
    },
    {
      name: i18next.t('GENERAL.COST_EX_TAX'),
      propertyKey: 'cost',
      type: 'currency',
    },
    {
      name: i18next.t('GENERAL.IS_CASHIER_LOSS'),
      propertyKey: 'isCashierLoss',
      type: 'boolean',
    },
    {
      propertyKey: 'total',
      name: i18next.t('GENERAL.TOTAL_EX_TAX'),
      transform: (value) => formatNumberToExcelUsage(value, currency.numberDecimals),
    },
    {
      propertyKey: 'timestamp',
      name: i18next.t('GENERAL.DATE'),
    },
  ];

  if (isCentralMode) {
    columns.splice(3, 0, {
      propertyKey: 'cost',
      name: i18next.t('GENERAL.COST_EX_TAX'),
    });
  }

  if (hasMultipleChannels && !isCentralMode) {
    columns.splice(3, 0, {
      propertyKey: 'channel',
      name: i18next.t('GENERAL.CHANNEL'),
    });
  }

  return columns;
};

export const formattedRecipesSheetData = (
  recipesLosses,
  lossCategories,
  channels,
  defaultChannelId,
) =>
  recipesLosses.map(
    ({
      name,
      channelId,
      quantity,
      lossCategoryId,
      total,
      timestamp,
      costByChannelId,
      unit,
      storeName,
      isCashierLoss,
    }) => {
      const selectedChannel = channels.find(({ id }) => id === channelId) || defaultChannelId;

      const category = lossCategories.find(({ id }) => id === lossCategoryId);

      const currentCostByChannelId = costByChannelId[channelId];

      const cost = currentCostByChannelId || '';

      return {
        storeName,
        name,
        quantity,
        unit,
        channel: selectedChannel.name,
        category: category.name || i18next.t('GENERAL.NONE_VALUE'),
        total,
        cost,
        timestamp: moment(timestamp).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
        isCashierLoss,
      };
    },
  );

const getInformationsSheetColumns = (clientStoreName) => [
  {
    propertyKey: 'stores',
    name: clientStoreName,
  },
  {
    propertyKey: 'startDate',
    name: i18next.t('LOSSES.LIST.EXPORT_COLUMN_PERIOD_START'),
  },
  {
    propertyKey: 'endDate',
    name: i18next.t('LOSSES.LIST.EXPORT_COLUMN_PERIOD_END'),
  },
];

const getInformationsSheetData = (selectedStores, startDate, endDate) => [
  {
    stores: selectedStores.map(({ name }) => name).join(', '),
    startDate: moment(startDate).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    endDate: moment(endDate).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
  },
];

export const getLossesExportXLS = ({
  supplierProductsLosses,
  currency,
  lossCategories,
  isCentralMode,
  hasMultipleChannels,
  recipesLosses,
  channels,
  clientStoreName,
  selectedStores,
  startDate,
  endDate,
  defaultChannelId,
}) => {
  const supplierProductsColumns = getSupplierProductsSheetColumns(currency);

  const supplierProductSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.SUPPLIER_PRODUCT_PLURAL'),
    supplierProductsColumns,
    formattedSupplierProductsSheetData(supplierProductsLosses, lossCategories),
    currency,
  );

  const recipesColumns = getRecipesSheetColumns(isCentralMode, hasMultipleChannels, currency);

  const recipesSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.RECIPE_PLURAL'),
    recipesColumns,
    formattedRecipesSheetData(recipesLosses, lossCategories, channels, defaultChannelId),
    currency,
  );

  const informationsColumns = getInformationsSheetColumns(clientStoreName);

  const informationsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INFORMATIONS'),
    informationsColumns,
    getInformationsSheetData(selectedStores, startDate, endDate),
    currency,
  );

  utilsXLS.makeXLS(
    `${i18next.t(
      isCentralMode
        ? 'LOSSES.LIST.EXPORT_FILE_NAME_CENTRAL_KITCHEN'
        : 'LOSSES.LIST.EXPORT_FILE_NAME',
      {
        date: moment().format(DATE_DISPLAY_FORMATS.DASHED_DAY_MONTH_YEAR),
      },
    )}`,
    [supplierProductSheet, recipesSheet, informationsSheet],
  );
};
